import React, { useEffect, useMemo, useState } from 'react';
import { Pill, Search, X, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Scale } from 'lucide-react';
import ToolHeader from '../shared/ToolHeader';
import { Card, Section } from '../hospital/shared/primitives';
import { Segmented } from '../hospital/shared/protocolBits';
import { CATEGORIES, CLASSES, drugsIn, drugById, searchDrugs, computeDose } from '../../data/clinicalTools/drugReference';
import { trackClinicalToolView } from '../../utils/analyticsUtils';

/*
 * Medication Reference. Category, then class, then a list, then one card per drug with
 * the dose first and the pharmacology behind a tap. The pediatric category carries a
 * weight field; every per-kg dose line shows the computed dose beside it. Same pattern
 * as the Anticoagulation & Neuraxial tool. `initialCategory` lets the TCH page open it
 * straight on Pediatric.
 */

const DEFAULT_ACCENT = '#6d28d9';
const WEIGHT_KEY = 'drug-ref-weight-kg';

const readWeight = () => { try { return sessionStorage.getItem(WEIGHT_KEY) || ''; } catch { return ''; } };

/** Weight entry for the pediatric category, kept for the session */
const WeightField = ({ value, onChange }) => (
  <div className="flex items-center gap-3 rounded-xl bg-gray-100 dark:bg-gray-900 px-4 py-2.5">
    <Scale size={18} className="text-gray-400 flex-shrink-0" />
    <label htmlFor="drug-weight" className="text-sm text-gray-600 dark:text-gray-300 flex-shrink-0">Weight</label>
    <input
      id="drug-weight"
      type="number"
      inputMode="decimal"
      min="0"
      step="0.1"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="kg"
      className="flex-1 min-w-0 bg-transparent text-right text-[17px] font-semibold text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none"
    />
    <span className="text-sm text-gray-500 dark:text-gray-400">kg</span>
    {value && (
      <button onClick={() => onChange('')} aria-label="Clear weight" className="p-1 -mr-1 text-gray-400"><X size={16} /></button>
    )}
  </div>
);

const Rows = ({ rows, kg, accent }) => rows.map((r) => {
  const computed = kg ? computeDose(r.text, kg) : null;
  return (
    <div key={r.label + r.text} className="px-4 py-2.5 flex items-baseline justify-between gap-4">
      <span className="text-sm text-gray-600 dark:text-gray-300 flex-shrink-0">{r.label}</span>
      <span className="text-right min-w-0">
        <span className="text-[15px] text-gray-900 dark:text-white">{r.text}</span>
        {computed && <span className="block text-[15px] font-semibold mt-0.5" style={{ color: accent }}>{computed}</span>}
      </span>
    </div>
  );
});

const Receptors = ({ receptors }) => (
  <div className="px-4 py-3">
    <div className="eyebrow mb-1.5">Receptors</div>
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {Object.entries(receptors).map(([r, v]) => (
        <span key={r} className="text-sm"><span className="text-gray-600 dark:text-gray-300">{r}</span> <span className="font-semibold text-gray-900 dark:text-white">{v}</span></span>
      ))}
    </div>
  </div>
);

const Bullets = ({ items }) => (
  <ul className="space-y-1">
    {items.map((t) => <li key={t} className="text-sm text-gray-900 dark:text-white leading-snug pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[0.55em] before:w-1 before:h-1 before:rounded-full before:bg-gray-400">{t}</li>)}
  </ul>
);

const DrugCard = ({ drug, kg, accent }) => {
  const [pharmOpen, setPharmOpen] = useState(false);
  useEffect(() => { setPharmOpen(false); }, [drug]);
  const { pk, pd, uses, notes } = drug.pharm;
  const hasPharm = pk.length || pd.length || uses.length || notes.length;
  return (
    <Card>
      <div className="px-4 py-3">
        <div className="text-[17px] font-semibold text-gray-900 dark:text-white leading-tight">{drug.name}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{[drug.brand, drug.clsLabel].filter(Boolean).join(' · ')}</div>
        {drug.bestFor && <div className="text-sm text-gray-700 dark:text-gray-300 mt-2 leading-snug">{drug.bestFor}</div>}
      </div>
      {drug.receptors && <Receptors receptors={drug.receptors} />}
      {drug.doses.length > 0 && (
        <>
          <div className="px-4 pt-3 pb-1"><div className="eyebrow">Dose</div></div>
          <Rows rows={drug.doses} kg={kg} accent={accent} />
        </>
      )}
      {drug.max && (
        <>
          <div className="px-4 pt-3 pb-1"><div className="eyebrow">Maximum</div></div>
          <Rows rows={drug.max} kg={kg} accent={accent} />
        </>
      )}
      {drug.cautions.length > 0 && (
        <div className="px-4 py-3">
          <div className="eyebrow mb-1.5">Cautions</div>
          <Bullets items={drug.cautions} />
        </div>
      )}
      {hasPharm ? (
        <>
          <button onClick={() => { setPharmOpen((o) => !o); }} aria-expanded={pharmOpen} className="w-full px-4 py-3 flex items-center gap-3 text-left">
            <span className="flex-1 text-[15px] font-medium text-gray-900 dark:text-white">Pharmacology</span>
            {pharmOpen ? <ChevronUp size={18} className="text-gray-300 dark:text-gray-600" /> : <ChevronDown size={18} className="text-gray-300 dark:text-gray-600" />}
          </button>
          {pharmOpen && (
            <div className="divide-y divide-gray-100 dark:divide-gray-700/70">
              {pk.length > 0 && <div><div className="px-4 pt-3 pb-1"><div className="eyebrow">Pharmacokinetics</div></div><Rows rows={pk} /></div>}
              {pd.length > 0 && <div><div className="px-4 pt-3 pb-1"><div className="eyebrow">Pharmacodynamics</div></div><Rows rows={pd} /></div>}
              {uses.length > 0 && <div className="px-4 py-3"><div className="eyebrow mb-1.5">Uses</div><Bullets items={uses} /></div>}
              {notes.length > 0 && <div className="px-4 py-3"><div className="eyebrow mb-1.5">Notes</div><Bullets items={notes} /></div>}
            </div>
          )}
        </>
      ) : null}
    </Card>
  );
};

const MedicationReferenceScreen = ({ onBack, initialCategory = 'cardiovascular', backLabel = 'Clinical Tools', lockCategory = false, accent = DEFAULT_ACCENT }) => {
  const [category, setCategory] = useState(initialCategory);
  const [cls, setCls] = useState(CLASSES[initialCategory][0].id);
  const [drugId, setDrugId] = useState(null);
  const [query, setQuery] = useState('');
  const [weight, setWeightState] = useState(readWeight);

  useEffect(() => { trackClinicalToolView('Medication Reference'); }, []);

  const setWeight = (v) => {
    setWeightState(v);
    try { if (v) sessionStorage.setItem(WEIGHT_KEY, v); else sessionStorage.removeItem(WEIGHT_KEY); } catch { /* ignore */ }
  };
  const kg = category === 'pediatric' ? parseFloat(weight) || 0 : 0;

  const searching = query.trim().length > 0;
  const matches = useMemo(() => (searching ? searchDrugs(query) : []), [query, searching]);
  const list = drugsIn(category, cls);
  const drug = drugId ? drugById(drugId) : null;

  const pickCategory = (id) => { setCategory(id); setCls(CLASSES[id][0].id); setDrugId(null); setQuery(''); };
  const pickClass = (id) => { setCls(id); setDrugId(null); };
  const pickDrug = (id) => { setDrugId(id); };
  const pickMatch = (d) => { setCategory(d.category); setCls(d.cls); setDrugId(d.id); setQuery(''); };

  const classes = CLASSES[category];
  const title = lockCategory ? `${CATEGORIES.find((c) => c.id === category)?.label || ''} Medications`.replace(/^CV /, 'Cardiovascular ') : 'Medication Reference';

  return (
    <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      <ToolHeader title={title} icon={Pill} accent={accent} backLabel={backLabel} onBack={onBack} />

      <div className="bg-white dark:bg-gray-800 px-4 pb-3 border-b border-gray-200 dark:border-gray-700">
        {!lockCategory && (
          <>
            <div className="eyebrow mb-1.5 px-1">Category</div>
            <Segmented items={CATEGORIES} value={category} onChange={pickCategory} />
          </>
        )}
        <div className="eyebrow mt-4 mb-1.5 px-1">Search</div>
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search any drug (generic or brand name)"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            className="w-full pl-11 pr-10 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-900 text-[15px] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600"
          />
          {query && (
            <button onClick={() => setQuery('')} aria-label="Clear" className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400"><X size={16} /></button>
          )}
        </div>
        {!searching && (
          <>
            <div className="eyebrow mt-4 mb-1.5 px-1">Browse by class</div>
            <div className="flex flex-wrap gap-2">
              {classes.map((c) => {
                const active = c.id === cls;
                return (
                  <button
                    key={c.id}
                    onClick={() => pickClass(c.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${active ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900 shadow-sm' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </>
        )}
        {category === 'pediatric' && !searching && (
          <div className="mt-4"><WeightField value={weight} onChange={setWeight} /></div>
        )}
      </div>

      <div className="p-6 space-y-5">
        {searching ? (
          <Card>
            {matches.length === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">No match. Try the generic or brand name.</p>
            ) : matches.map((d) => (
              <button key={d.id} onClick={() => pickMatch(d)} className="w-full px-4 py-3 flex items-center gap-3 text-left">
                <span className="flex-1 min-w-0">
                  <span className="block text-[15px] font-medium text-gray-900 dark:text-white">{d.name}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{[d.brand, CATEGORIES.find((c) => c.id === d.category)?.label, d.clsLabel].filter(Boolean).join(' · ')}</span>
                </span>
                <ChevronRight size={18} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
              </button>
            ))}
          </Card>
        ) : drug ? (
          <>
            <button onClick={() => { setDrugId(null); }} className="flex items-center gap-1 text-sm font-medium" style={{ color: accent }}>
              <ChevronLeft size={16} /> All {drug.clsLabel.toLowerCase()}
            </button>
            <DrugCard drug={drug} kg={kg} accent={accent} />
            {kg > 0 && (
              <p className="px-1 text-xs text-gray-500 dark:text-gray-400">Computed doses are for {kg} kg and respect any stated maximum. Check the concentration you draw from.</p>
            )}
          </>
        ) : (
          <Section title={classes.find((c) => c.id === cls)?.label}>
            <Card>
              {list.map((d) => (
                <button key={d.id} onClick={() => pickDrug(d.id)} className="w-full px-4 py-3 flex items-center gap-3 text-left">
                  <span className="flex-1 min-w-0">
                    <span className="block text-[15px] font-medium text-gray-900 dark:text-white">{d.name}</span>
                    {d.brand && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{d.brand}</span>}
                  </span>
                  <ChevronRight size={18} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
                </button>
              ))}
            </Card>
          </Section>
        )}
        <p className="px-1 text-xs text-gray-500 dark:text-gray-400">Sources for this content are being added. Verify against the label and institutional policy.</p>
      </div>
      <div className="h-8" />
    </div>
  );
};

export default MedicationReferenceScreen;
